import { DateTime } from 'luxon';
import React, { useRef, useState } from 'react';
import { forwardRef } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { Translate, useTranslate } from '~/i18n';
import { CalendarIcon } from '~/svgs/navbar';
import css from './styles.module.css';
import cn from 'classnames';
import { AngelLeftIcon, AngelRightIcon } from '~/svgs/common';
import ar from 'date-fns/locale/ar-SA';
import Button from '../common/button';
import useOnClickOutside from '~/hooks/on-click-outside';
import { useRouter } from 'next/router';

registerLocale('ar', ar);

type DatesFilterProps = {
   // eslint-disable-next-line no-unused-vars
   dateKeydown: (e: React.KeyboardEvent) => void;
   // eslint-disable-next-line no-unused-vars
   onApply: (dates: { start: Date; end: Date } | null) => void;
};

type Dates = {
   start: Date | null;
   end: Date | null;
};
export const DatesFilter = ({ dateKeydown, onApply }: DatesFilterProps) => {
   const { locale, dir } = useTranslate();
   const [open, setOpen] = useState(false);
   const datePickerRef = useRef<any>(null);
   const router = useRouter();
   const [date, setDate] = useState<Dates>({
      start: router.query.start_date ? new Date(String(router.query.start_date)) : null,
      end: router.query.start_date ? new Date(String(router.query.end_date)) : null,
   });
   const [appliedDate, setAppliedDate] = useState<Dates>({
      start: router.query.start_date ? new Date(String(router.query.start_date)) : null,
      end: router.query.start_date ? new Date(String(router.query.end_date)) : null,
   });
   const updateApplied = () => {
      if (date.start && date.end) {
         setOpen(false);
         setAppliedDate({ ...date });
         onApply({ start: date.start, end: date.end });
      }
   };

   const clear = () => {
      setDate({ start: null, end: null });
      setAppliedDate({ start: null, end: null });
      onApply(null);
   };

   useOnClickOutside(datePickerRef, () => setOpen(false));

   const CustomDateInput = forwardRef<any, any>((_, ref) => {
      const start = appliedDate?.start
         ? DateTime.fromJSDate(appliedDate?.start).toFormat('LLL dd')
         : '';
      const end = appliedDate?.end ? DateTime.fromJSDate(appliedDate?.end).toFormat('LLL dd') : '';

      const keydown = (e: React.KeyboardEvent) => {
         e.preventDefault();
         switch (e.code) {
            case 'ArrowDown':
               return !open && setOpen(true);

            case 'Escape':
               return open && setOpen(false);

            default:
         }
         dateKeydown(e);
      };

      return (
         <button
            id="datepicker-btn"
            type="button"
            onClick={() => setOpen(prev => !prev)}
            onKeyDown={keydown}
            ref={ref}
            className="flex items-center h-full px-6 bg-white focus:outline-none focus:ring-2 focus:ring-primary-color tablet:!rounded-none ltr:rounded-r-full rtl:rounded-l-full">
            <div className="px-2">
               <CalendarIcon />
            </div>
            <span className={cn('font-bold', start && end ? 'text-xs' : 'text-sm')}>
               {start && end ? start + ' - ' + end : <Translate id="filters:add dates" />}
            </span>
         </button>
      );
   });
   CustomDateInput.displayName = 'DatePickerInput';

   const min_date = new Date();
   const max_date = new Date();

   max_date.setMonth(min_date.getMonth() + 2);

   return (
      <div ref={datePickerRef} className="relative h-full">
         <ReactDatePicker
            ref={datePickerRef}
            selected={date.start}
            startDate={date.start}
            endDate={date.end}
            showPopperArrow={false}
            wrapperClassName={css.datepicker}
            selectsRange
            minDate={min_date}
            maxDate={max_date}
            shouldCloseOnSelect={false}
            locale={locale}
            popperPlacement="bottom-start"
            open={open}
            className="z-30"
            weekDayClassName={() => 'opacity-40'}
            todayButton={
               <div
                  onClick={e => e.stopPropagation()}
                  className="flex items-center justify-between h-full px-4 py-3 border-t">
                  <Button
                     onClick={clear}
                     type="secondary"
                     className="!border-transparent !shadow-none font-bold text-base !py-2">
                     <Translate id="common:clear" />
                  </Button>
                  <Button
                     onClick={updateApplied}
                     className="!border-transparent !shadow-none font-bold !py-2 px-6 text-base">
                     <Translate id="common:apply" />
                  </Button>
               </div>
            }
            formatWeekDay={name => (locale === 'ar' ? name : name.substr(0, 3))}
            onChange={([start, end]: any) => {
               setDate({ start, end });
            }}
            renderCustomHeader={({
               decreaseMonth,
               increaseMonth,
               prevMonthButtonDisabled,
               nextMonthButtonDisabled,
               date,
            }) => (
               <div className="flex justify-between px-3 mt-2 mb-4">
                  <button
                     type="button"
                     aria-label="previous month"
                     disabled={prevMonthButtonDisabled}
                     onClick={decreaseMonth}>
                     {dir === 'ltr' ? <AngelLeftIcon /> : <AngelRightIcon />}
                  </button>
                  <div className="flex-grow text-base">
                     {DateTime.fromJSDate(date).toFormat('LLLL yyyy')}
                  </div>
                  <button
                     type="button"
                     aria-label="next month"
                     disabled={nextMonthButtonDisabled}
                     onClick={increaseMonth}>
                     {dir === 'ltr' ? <AngelRightIcon /> : <AngelLeftIcon />}
                  </button>
               </div>
            )}
            customInput={<CustomDateInput date={date} />}
         />
      </div>
   );
};
